import {
  APICallState,
  Selectors,
  StatusOverlay,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useDispatch, useSelector} from 'Provider';
import {View} from 'native-base';
import React, {useCallback, useEffect} from 'react';

import PaymentInfoModule from 'components/PaymentInfoModule';
import PaymentInfoEmptyStateModule from 'components/PaymentInfoModule/EmptyState/PaymentInfoEmptyStateModule';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {
  PAYMENT_MODULE_STATE,
  ProfileActions,
  paymentModuleStateSelector,
} from 'store/profile';
import ConnectedFreedomPayModule from 'containers/ConnectedFreedomPayModule';
import {scrollToContentTop} from 'screens/Profile';
import {
  deleteCardCallStateSelector,
  getAllCardsCallStateSelector,
  saveCardCallStateSelector,
  saveCardResponseErrorSelector,
  savedCardsSelector,
} from 'store/payments/payments.selectors';
import {
  PaymentCardInfo,
  PaymentsActions,
} from 'store/payments/payments.reducer';
import WarningModal from 'components/WarningModal';

interface ConnectedPaymentInfoModuleProps {
  setTabToProfile: () => void;
}

const ConnectedPaymentInfoModule: React.FC<ConnectedPaymentInfoModuleProps> = ({
  setTabToProfile,
}) => {
  const dispatch = useDispatch();

  const hasAddress = useWidgetState(Selectors.isAddressValidSelector);
  const paymentModuleState = useSelector(paymentModuleStateSelector);
  const saveCardCallState = useSelector(saveCardCallStateSelector);
  const getAllCardsCallState = useSelector(getAllCardsCallStateSelector);
  const {trackAdobeEvent} = useTrackEvent();
  const savedCards = useSelector(savedCardsSelector);
  const hasAvailablePayments = savedCards.length > 0;
  const deleteCardCallState = useSelector(deleteCardCallStateSelector);
  const saveCardResponseError = useSelector(saveCardResponseErrorSelector);
  const displaySaveCardError = Boolean(
    saveCardResponseError &&
      saveCardResponseError.resultCode === 'WCPS0011' &&
      saveCardResponseError.resultMessage
  );

  useEffect(() => {
    dispatch(PaymentsActions.getAllCards());
  }, [dispatch]);

  const onAddEditPayment = () => {
    scrollToContentTop();
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'my account payment method update'},
    });
    dispatch(PaymentsActions.resetSaveCardCallState());
    dispatch(
      ProfileActions.updatePaymentModuleState(PAYMENT_MODULE_STATE.IS_ADDING)
    );
    dispatch(PaymentsActions.resetDeleteCardCallState());
  };

  const onOverlayAnimationFinish = () => {
    dispatch(
      ProfileActions.updatePaymentModuleState(PAYMENT_MODULE_STATE.DEFAULT)
    );

    dispatch(PaymentsActions.resetSaveCardCallState());
  };

  const handleDeletePayment = useCallback(
    (card: PaymentCardInfo) => {
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {name: 'payment method updates saved'},
      });

      dispatch(PaymentsActions.deleteCard(card));
    },
    [dispatch, trackAdobeEvent]
  );

  useEffect(() => {
    dispatch(PaymentsActions.resetDeleteCardCallState());
  }, [dispatch]);

  return (
    <View width="100%">
      {saveCardCallState !== APICallState.INITIAL && !displaySaveCardError && (
        <StatusOverlay
          apiCallState={saveCardCallState}
          panelTitle={'Payment Information'} // May need copy clarification in the future
          onOverlayAnimationFinish={onOverlayAnimationFinish}
        />
      )}
      {/* TODO: add back address check for hidden state module */}
      {(hasAvailablePayments &&
        paymentModuleState === PAYMENT_MODULE_STATE.DEFAULT) ||
      getAllCardsCallState === APICallState.LOADING ? (
        <>
          <PaymentInfoModule
            savedCards={savedCards}
            handleDeletePayment={handleDeletePayment}
            onAddPayment={onAddEditPayment}
            getAllCardsCallState={getAllCardsCallState}
            deleteCardCallState={deleteCardCallState}
            onResetDeleteCardCallState={() =>
              dispatch(PaymentsActions.resetDeleteCardCallState())
            }
          />
        </>
      ) : paymentModuleState === PAYMENT_MODULE_STATE.IS_ADDING ? (
        <ConnectedFreedomPayModule />
      ) : (
        <PaymentInfoEmptyStateModule onAddPaymentPressed={onAddEditPayment} />
      )}
      <WarningModal
        isOpen={displaySaveCardError}
        onClose={onOverlayAnimationFinish}
        primaryCTATitle="Ok"
        body={saveCardResponseError?.resultMessage}
        title="Unable to save your card"
        onPrimaryPress={onOverlayAnimationFinish}
      />
    </View>
  );
};

export default ConnectedPaymentInfoModule;
